import React, { useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { MessageCircle } from 'lucide-react';

const TaskCard = ({ task, onTaskClick, onApply, onMoveToColumn }) => {
  const controls = useAnimation();
  const draggedRef = useRef(false);

  const handleDragEnd = async (event, info) => {
    // Find target column under the pointer
    const x = info.point.x;
    const y = info.point.y;
    const el = document.elementFromPoint(x, y);
    const colEl = el?.closest('[data-column-id]');
    const targetColumnId = colEl?.dataset.columnId;

    if (targetColumnId && targetColumnId !== task.column_id) {
      // Successful drop on a different column — let parent handle state update.
      // Snap to origin first so the card returns home before re-rendering in the new column.
      controls.start({ x: 0, y: 0, transition: { duration: 0 } });
      onMoveToColumn(task.id, targetColumnId);
    } else {
      // Same column or invalid drop — animate back smoothly
      controls.start({ x: 0, y: 0, transition: { type: 'spring', stiffness: 500, damping: 35 } });
    }

    // small delay before allowing click again
    setTimeout(() => { draggedRef.current = false; }, 50);
  };

  const handleClick = () => {
    if (draggedRef.current) return;
    onTaskClick(task);
  };

  return (
    <motion.div
      layout
      drag
      dragMomentum={false}
      dragElastic={0}
      animate={controls}
      whileDrag={{ scale: 1.04, zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
      onDragStart={() => { draggedRef.current = true; }}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      className="p-3 mb-2 bg-white border border-gray-200 rounded-lg cursor-grab active:cursor-grabbing hover:border-gray-300 hover:shadow-sm transition-shadow"
    >
      <h3 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2">{task.title}</h3>
      {task.description && (
        <p className="text-xs text-gray-500 line-clamp-2 mb-2">
          {task.description}
        </p>
      )}

      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {task.tags.map((tag, index) => (
            <span
              key={index}
              className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {(task.type === 'suggestion' || (task.comments && task.comments.length > 0)) && (
        <div className="flex justify-between items-center mt-2">
          {task.type === 'suggestion' ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onApply(task.id);
              }}
              className="text-xs text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline"
            >
              Apply
            </button>
          ) : <span />}
          {task.comments && task.comments.length > 0 && (
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <MessageCircle size={11} />
              {task.comments.length}
            </span>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default TaskCard;
